import { graphql, useQuery } from "@opensea/graphql"
import { FlexColumn, List } from "@opensea/ui-kit"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import type { Address } from "../../../../../types"
import {
  ViewWalletPortfolioValue,
  ViewWalletPortfolioValueSkeleton,
} from "../../ViewWalletPortfolioValue"
import { ViewWalletPortfolioValueFragment } from "../../ViewWalletPortfolioValue.fragment"
import {
  ProfileCollectionItem,
  ProfileCollectionItemSkeleton,
} from "./ProfileCollectionItem"
import { ProfileCollectionItemFragment } from "./ProfileCollectionItem.fragment"

export const NftTabQuery = graphql(
  `
    query NftTabQuery($address: Address!, $cursor: String, $limit: Int) {
      profileByAddress(address: $address) {
        __typename
        ... on Profile {
          portfolioSummary {
            estimatedNftValue {
              ...ViewWalletPortfolioValue
            }
          }
        }
      }

      profileCollections(
        addresses: [$address]
        cursor: $cursor
        limit: $limit
      ) {
        items {
          collection {
            id
            ...ProfileCollectionItem
          }
          ownership {
            itemCount
          }
        }
      }
    }
  `,
  [ViewWalletPortfolioValueFragment, ProfileCollectionItemFragment],
)

export type NftTabProps = {
  address: Address
}

export function NftTab({ address }: NftTabProps) {
  const t = useTranslations("wallet.NftTab")
  const [{ data }] = useQuery({
    query: NftTabQuery,
    variables: {
      address,
    },
  })

  if (
    !data?.profileByAddress ||
    data.profileByAddress.__typename !== "Profile"
  ) {
    return <>{t("errorMessage")}</>
  }

  const profileCollections = data.profileCollections.items

  return (
    <FlexColumn className="gap-6 px-2 lg:px-4">
      <ViewWalletPortfolioValue
        className="px-2"
        portfolioValue={
          data.profileByAddress.portfolioSummary.estimatedNftValue
        }
      />
      <ManageInProfileButton />
      <List className="*:!rounded-md bg-transparent pb-8" showBorder={false}>
        {/* TODO(jihok): collection order non-deterministic */}
        {profileCollections.map(({ collection, ownership }) => (
          <ProfileCollectionItem
            collection={collection}
            itemCount={ownership.itemCount}
            key={collection.id}
          />
        ))}
      </List>
    </FlexColumn>
  )
}

const ManageInProfileButton = () => {
  const t = useTranslations("wallet.NftTab")
  return (
    <Button className="mx-2" href="/profile" variant="secondary">
      {t("manageInProfile")}
    </Button>
  )
}

export function NftTabSkeleton({ items = 5 }: { items?: number }) {
  return (
    <FlexColumn className="gap-6 px-2 lg:px-4">
      <ViewWalletPortfolioValueSkeleton />
      <ManageInProfileButton />
      <List className="*:!rounded-md bg-transparent" showBorder={false}>
        {new Array(items).fill(0).map((_, i) => (
          <ProfileCollectionItemSkeleton key={i} />
        ))}
      </List>
    </FlexColumn>
  )
}
